<template>
    <div class="join-by-code d-flex flex-column align-items-center">
        <QrScanner v-if="scanning" @detected="on_scanned" @error="on_scanner_error" @close="scanning = false" />

        <h2 class="join-by-code-title">Introduce el código</h2>
        <p class="join-by-code-subtitle">El código lo muestra la pantalla del organizador</p>

        <input
            ref="codeInput"
            aria-label="Código del evento"
            autocomplete="one-time-code"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="6"
            placeholder="000000"
            class="form-control form-control-lg join-by-code-input text-center"
            v-model="code"
            :disabled="checking"
            @keyup.enter="submit">

        <button type="button" class="btn btn-warning join-by-code-submit mt-3" :disabled="!code || checking" @click="submit">
            {{ checking ? 'Comprobando...' : 'Unirse' }}
        </button>

        <button type="button" class="btn btn-link join-by-code-scan mt-2" @click="scanning = true">
            <i class="bi bi-qr-code-scan me-1"></i> Escanear código QR
        </button>
    </div>
</template>

<script>
import QrScanner from '@/components/QrScanner.vue'

// Guest-facing join gate: either type the short numeric code shown on the
// organizer's QR screen, or scan that QR directly with the device camera.
// Reused from both Home.vue (idle screen) and Auth.vue (deep-link
// "/join/<code>" landing) - see server.js's validate_join_code for how the
// code is checked against the whole-event or currently live stage code.
export default {
    name: 'JoinByCode',
    components: {
        QrScanner,
    },
    props: {
        initial_code: {
            type: String,
            default: null,
        },
        auto_submit: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['verified'],
    data(){
        return {
            code: this.initial_code || '',
            checking: false,
            scanning: false,
        }
    },
    watch: {
        '$store.state.network'(value) { if (value === 'connected' && !this.auto_submit) this.$nextTick(() => this.$refs.codeInput?.focus({ preventScroll: true })); },
        '$store.state.join_code_result' (result){
            if(!this.checking || result == null)
                return;

            this.checking = false;

            if(result.valid){
                window.store.commit('setJoinVerified', true);
                if(window.router.currentRoute._value.name != 'Auth')
                    window.router.push({name: 'Auth'});

                var joined_name = result.stage_name || result.season_name;
                window.toast.success(joined_name ? ('¡Te has unido a ' + joined_name + '!') : '¡Código válido!');
                this.$emit('verified', result);
            }else{
                window.toast.error('Código no válido. Comprueba que la etapa siga activa.');
            }
        }
    },
    mounted(){
        if (!this.auto_submit) this.$nextTick(() => this.$refs.codeInput?.focus({ preventScroll: true }));
        if(this.initial_code && this.auto_submit)
            this.try_auto_submit();
    },
    unmounted(){
        if(this._stop_auto_submit_watch)
            this._stop_auto_submit_watch();
    },
    methods: {
        // Reaching this component (Auth.vue mounts it as soon as
        // needs_code_gate is true, regardless of whether ConnectionOverlay
        // is visually covering it) doesn't mean network.js has finished
        // opening the websocket yet - network.send() drops the message
        // silently if it hasn't, which used to leave "Comprobando..."
        // spinning forever with no way out. Wait for the connection instead
        // of racing it.
        try_auto_submit(){
            if(window.store.state.network === 'connected'){
                this.submit();
                return;
            }

            this._stop_auto_submit_watch = this.$watch('$store.state.network', (value) => {
                if(value !== 'connected')
                    return;

                this._stop_auto_submit_watch();
                this._stop_auto_submit_watch = null;
                this.submit();
            });
        },
        submit(){
            if(!this.code || this.checking)
                return;

            if(window.store.state.network !== 'connected'){
                window.toast.warning('Conectando con el servidor, inténtalo de nuevo en un momento...');
                return;
            }

            this.checking = true;
            window.network.send(window.RPC.VALIDATE_JOIN_CODE, {code: this.code});
        },
        on_scanned(text){
            this.scanning = false;
            const match = String(text).match(/\/join\/([0-9]{4,8})(?:[/?#]|$)/i);
            this.code = match ? match[1] : String(text).trim();
            this.submit();
        },
        on_scanner_error(message){
            this.scanning = false;
            window.toast.warning(message);
        }
    }
}
</script>

<style scoped>
.join-by-code-title{
    font-family: "Archivo", sans-serif;
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
}
.join-by-code-subtitle{
    font-size: 0.85rem;
    color: var(--vc-text-600, #999);
    margin-bottom: 1.25rem;
}
.join-by-code-input{
    max-width: 220px;
    font-family: "Archivo", sans-serif;
    font-weight: 800;
    font-size: 1.6rem;
    letter-spacing: 0.2em;
}
.join-by-code-submit{
    min-width: 220px;
}
</style>
